import { Link } from 'react-router-dom'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import Footer from '../components/Footer'

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="/branding/cte-course-platform-icon.svg" 
                alt="CTE Course Platform" 
                className="h-8 w-8"
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-cte-blue to-cte-blue-dark bg-clip-text text-transparent">
                CTE Course Platform
              </span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link
                to="/login"
                className="text-sm text-gray-600 hover:text-cte-blue transition-colors"
              >
                Sign In
              </Link>
              <Link
                to="/#pricing"
                className="text-sm bg-cte-blue text-white px-4 py-2 rounded-lg hover:bg-cte-blue-dark transition-colors"
              >
                Enroll Now
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-4xl mx-auto py-8 px-4 w-full bg-white my-8 rounded-lg shadow-sm">
      <Link
        to="/"
        className="inline-flex items-center text-cte-blue hover:text-cte-blue-dark mb-6"
      >
        <ArrowLeftIcon className="w-4 h-4 mr-2" />
        Back to Home
      </Link>

      <h1 className="text-4xl font-bold text-cte-black mb-4">Privacy Policy</h1>
      <p className="text-gray-600 mb-8">
        <strong>Effective Date:</strong> November 2025<br />
        <strong>Last Updated:</strong> November 14, 2025<br />
        <strong>Company:</strong> CTE Consultants & General Sales LLC
      </p>

      <div className="prose prose-slate max-w-none">
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-cte-black mb-4">1. Introduction</h2>
          <p className="text-gray-700 mb-4">
            CTE Course Platform ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Platform.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-cte-black mb-4">2. Information We Collect</h2>
          
          <h3 className="text-xl font-semibold text-cte-black mb-3 mt-4">Personal Information</h3>
          <p className="text-gray-700 mb-2">We collect information that you provide directly to us, including:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
            <li>Name and email address (during registration)</li>
            <li>Profile information</li>
            <li>Course progress and completion data</li>
            <li>Submissions and assignments</li>
            <li>Communication preferences</li>
          </ul>

          <h3 className="text-xl font-semibold text-cte-black mb-3 mt-4">Usage Information</h3>
          <p className="text-gray-700 mb-2">We automatically collect certain information when you use the Platform:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>Log data (IP address, browser type, pages visited)</li>
            <li>Device information</li>
            <li>Cookies and similar tracking technologies</li>
            <li>Learning analytics (time spent, modules accessed)</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-cte-black mb-4">3. How We Use Your Information</h2>
          <p className="text-gray-700 mb-2">We use the information we collect to:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>Provide, maintain, and improve our Platform</li>
            <li>Process your registration and manage your account</li>
            <li>Track your course progress and issue certificates</li>
            <li>Send you course updates and educational content</li>
            <li>Respond to your inquiries and provide customer support</li>
            <li>Analyze usage patterns to improve user experience</li>
            <li>Detect, prevent, and address technical issues</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-cte-black mb-4">4. Information Sharing and Disclosure</h2>
          <p className="text-gray-700 mb-4">
            We do not sell your personal information. We may share your information in the following circumstances:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li><strong>With your consent:</strong> When you explicitly agree to share information</li>
            <li><strong>Service providers:</strong> Third-party vendors who assist in operating our Platform</li>
            <li><strong>Legal requirements:</strong> When required by law or to protect our rights</li>
            <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-cte-black mb-4">5. Data Security</h2>
          <p className="text-gray-700 mb-4">
            We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>Encryption of data in transit and at rest</li>
            <li>Regular security assessments</li>
            <li>Access controls and authentication</li>
            <li>Secure backup procedures</li>
          </ul>
          <p className="text-gray-700 mt-4">
            However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-cte-black mb-4">6. Your Rights</h2>
          <p className="text-gray-700 mb-2">You have the right to:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li><strong>Access:</strong> Request a copy of your personal information</li>
            <li><strong>Correction:</strong> Update or correct inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information</li>
            <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
            <li><strong>Data portability:</strong> Receive your data in a structured format</li>
          </ul>
          <p className="text-gray-700 mt-4">
            To exercise these rights, please contact us at techsupport@cteconsultants.net
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-cte-black mb-4">7. Cookies</h2>
          <p className="text-gray-700 mb-4">
            We use cookies and similar tracking technologies to track activity on our Platform and store certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Platform.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-cte-black mb-4">8. Children's Privacy</h2>
          <p className="text-gray-700 mb-4">
            Our Platform is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-cte-black mb-4">9. Changes to This Privacy Policy</h2>
          <p className="text-gray-700 mb-4">
            We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-cte-black mb-4">10. Contact Us</h2>
          <p className="text-gray-700 mb-4">
            If you have any questions about this Privacy Policy, please contact us:
          </p>
          <p className="text-gray-700">
            Email: <a href="mailto:techsupport@cteconsultants.net" className="text-cte-blue hover:underline">techsupport@cteconsultants.net</a><br />
            Address: CTE Course Platform, Education Department
          </p>
        </section>
      </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  )
}
